// The driver record's body: the requirement list, the screening card and the event
// log. No header, no page chrome, no scroll container — the caller owns those.
//
// Split out of index.js so the merged user page can render the same record inside its
// Driver tab, under tabs instead of under a standalone header.
import React, { useState } from 'react';
// Not re-exported from ui/core; imported directly the way DriverBalanceCard already does.
import Collapse from '@mui/material/Collapse';
import { Button, Stack, Typography } from '../../ui/core';
import { hairline } from '../../ui/styles';
import config from '../../config';
import { ChevronDownIcon, EventIcon } from '../../ui/icons';
import { SectionCard, EmptyRows } from './components';
import RecordView from './RecordView';
import ScreeningCard from './ScreeningCard';
import { Box } from '../../ui/core';
import AddVehicleDialog from '../../components/widgets/DriverVehicles/AddVehicleDialog';

/**
 * The driver's Checkr candidate page.
 *
 * The background check is not a document and has no local detail worth building:
 * the report, its history and its adjudication all live at Checkr, so the row links
 * out rather than opening an inspector that could only ever paraphrase it.
 *
 * Null without a candidate id — a driver who has never been screened has no page to
 * open, and a link to nothing is worse than no link.
 */
export const checkrCandidateUrl = (candidateId) =>
  candidateId ? `${config.checkr.baseUrl.trim()}/candidates/${candidateId}` : null;

/** How many events are worth showing before the log stops being a section and becomes a page. */
const EVENT_PREVIEW = 8;

const EventRow = ({ entry }) => (
  <Stack
    direction="row"
    alignItems="baseline"
    spacing={1.5}
    sx={{ px: 1.75, py: 1, borderTop: (t) => `1px solid ${hairline(t)}` }}
  >
    {/* Which kind, in a fixed-width column so the labels line up and the eye can skip a
        kind without a filter to do it. */}
    <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', width: 62, flexShrink: 0 }}>{entry.kind}</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{entry.label}</Typography>
      {entry.detail && <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{entry.detail}</Typography>}
    </Box>
    {/* A dash rather than a dropped row: the event is real even when its stored timestamp
        is not parseable. */}
    <Typography sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}>{entry.at || '—'}</Typography>
  </Stack>
);

/**
 * The log, showing its newest events with the rest behind a toggle.
 *
 * A long-lived driver's log runs to 30+ rows — every application status change plus every
 * document decision — and it is the last section on the tab, so an un-truncated one turns
 * every visit to the Driver tab into a scroll past someone's entire history. The newest
 * events are the ones being read; the rest is archaeology.
 *
 * Truncated rather than scrolled inside a fixed height: a nested scroll area inside a page
 * that already scrolls is the thing that eats a trackpad gesture, and this section sits at
 * the bottom where a wheel event is most likely to be aimed past it.
 *
 * `Collapse` for the motion — MUI's own height transition, so it matches the easing and
 * duration of every other expanding thing in the app and honours `prefers-reduced-motion`
 * through the theme rather than through a second implementation here.
 */
const EventRows = ({ events }) => {
  const [expanded, setExpanded] = useState(false);
  const hidden = events.length - EVENT_PREVIEW;

  // Nothing to hide, so no control: a "Show all" that reveals nothing is worse than none.
  if (hidden <= 0) {
    return events.map((entry) => <EventRow key={entry.key} entry={entry} />);
  }

  return (
    <>
      {events.slice(0, EVENT_PREVIEW).map((entry) => (
        <EventRow key={entry.key} entry={entry} />
      ))}
      {/* `unmountOnExit`, so the collapsed rows are not in the DOM at all. Cmd+F would
          otherwise find text in a section that looks closed, which is the one thing a
          collapse must not do to a support tool. */}
      <Collapse in={expanded} unmountOnExit>
        {events.slice(EVENT_PREVIEW).map((entry) => (
          <EventRow key={entry.key} entry={entry} />
        ))}
      </Collapse>
      <Stack direction="row" justifyContent="center" sx={{ borderTop: (t) => `1px solid ${hairline(t)}` }}>
        <Button
          variant="text"
          size="small"
          onClick={() => setExpanded((open) => !open)}
          endIcon={
            <ChevronDownIcon
              sx={{
                fontSize: 15,
                transition: (t) => t.transitions.create('transform', { duration: t.transitions.duration.shortest }),
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary', py: 0.6, width: '100%' }}
        >
          {/* The count is the point: "Show more" does not say whether it is 2 rows or 40. */}
          {expanded ? 'Show fewer' : `Show ${hidden} older event${hidden === 1 ? '' : 's'}`}
        </Button>
      </Stack>
    </>
  );
};

const RecordPanel = ({
  profile,
  driver,
  mode,
  onModeChange,
  onSelect,
  screening,
  screeningError,
  events,
  header,
  busy,
  onAddVehicle,
  onRemoveVehicle,
  onGrandfatherVehicle,
}) => {
  const checkrUrl = checkrCandidateUrl(driver?.candidate_id);
  const [addingVehicle, setAddingVehicle] = useState(false);

  return (
    <>
      {header}

      <RecordView
        profile={profile}
        mode={mode}
        onModeChange={onModeChange}
        onSelect={onSelect}
        checkrUrl={checkrUrl}
        busy={busy}
        onAddVehicle={onAddVehicle ? () => setAddingVehicle(true) : null}
        onRemoveVehicle={onRemoveVehicle}
        onGrandfatherVehicle={onGrandfatherVehicle}
      />

      {onAddVehicle && (
        <AddVehicleDialog
          open={addingVehicle}
          onClose={() => setAddingVehicle(false)}
          onAdd={onAddVehicle}
          busy={busy}
        />
      )}

      {/* Same card as the driver and each vehicle above — these are sections of the
          same record, so they are the same object. */}
      <Stack spacing={2} sx={{ mt: 2 }}>
        <ScreeningCard
          history={screening}
          loading={!screening && !screeningError}
          error={screeningError}
          backgroundCheck={profile.backgroundCheck}
          checkrUrl={checkrUrl}
        />

        {/* One merged timeline rather than a control that switches between sources:
            the point of a log is what happened NEXT TO what, and a filter hides
            exactly that. All three sources are in it now — application status,
            documents, and standing changes with the reason and author a human
            typed. */}
        <SectionCard
          title="Event log"
          icon={<EventIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
          meta={
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', ml: 'auto' }}>
              {`${events.length} event${events.length === 1 ? '' : 's'}`}
            </Typography>
          }
        >
          {events.length ? <EventRows events={events} /> : <EmptyRows>Nothing recorded yet.</EmptyRows>}
        </SectionCard>
      </Stack>
    </>
  );
};

export default RecordPanel;
